import { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";

import { DateLike } from "~/lib/date";
import { isJobFinished } from "~/lib/jobs";
import LegacyDispatcher from "~/lib/legacyDispatcher";

import JobLogOutput from "~/components/job/Job/Log/Output";
import JobLogExitInformation from "~/components/job/Job/Log/ExitInformation";

import UserSessionStore from "~/stores/UserSessionStore";

import Emojify from "~/components/shared/Emojify";
import { useScrollFollow } from "~/components/shared/useScrollFollow";
import Icon from "~/components/shared/Icon";

import { Job } from "~/stores/JobLogStore";
import { Log, LogBody, LogControl, LogMessage } from "~/components/shared/Log";

import { useLineNumber } from "~/components/build/Show/lib/useLineNumber";
import { LogSearchProvider, LogSearch } from "~/components/job/Job/Log/LogSearch";
import classNames from "classnames";

const USER_SESSION_STORE_KEYS = {
  TIMESTAMPS_ON: "job-log-timestamps-on",
  THEME: "code-theme",
} as const;

type Props = {
  parentElementId: string;
  job: {
    id: string;
    basePath: string;
    state: string;
    passed: boolean;
    logDeletedMessage?: string;
    startedAt: DateLike | null | undefined;
    agentName: string | null | undefined;
    agentVersion: string | null | undefined;
    permissions: {
      deleteJobLog: {
        allowed: boolean;
      };
    };
  };
  autoFollow?: boolean;
};

type State = {
  following: boolean;
  theme: "default" | "solarized";
  linesHaveTimestamps?: boolean;
  timestampsOn?: boolean;
};

const getTheme = () => {
  const storedTheme = UserSessionStore.get(USER_SESSION_STORE_KEYS.THEME);
  return storedTheme === "default" || storedTheme === "solarized" ? storedTheme : "default";
};

export default function JobLog(props: Props) {
  const { group, lineNumber } = useLineNumber();
  const lineReference = lineNumber ?? group ?? null;
  const lineReferenceKey = lineReference === null ? null : `${props.job.id}:${lineReference}`;

  const [state, setState] = useState<State>({
    following: Boolean(lineReference === null && props.autoFollow),
    theme: getTheme(),
  });

  const disableFollow = useCallback(() => {
    setState((state) => ({ ...state, following: false }));
  }, []);

  const disabledLineReferenceRef = useRef(lineReferenceKey);
  useLayoutEffect(() => {
    if (lineReferenceKey === null) {
      disabledLineReferenceRef.current = null;
      return;
    }

    if (disabledLineReferenceRef.current !== lineReferenceKey) {
      disabledLineReferenceRef.current = lineReferenceKey;
      disableFollow();
    }
  }, [disableFollow, lineReferenceKey]);

  const [jumpToFailure, setJumpToFailure] = useState<{
    scrollToNext: () => number;
    count: number;
    current?: number;
  } | null>(null);

  const handleFailureMarkersChange = useCallback(
    (info: { count: number; current?: number; scrollToNext: () => number } | null) => {
      setJumpToFailure(info);
    },
    [],
  );

  const isTimestampsOn = useMemo(() => {
    // If timestamps aren't even available in this log, don't bother doing anythingf else
    if (!state.linesHaveTimestamps) {
      return false;
    }

    // If timestmaps haven't been manually turned on in this log, use what ever
    // they chose last.
    if (state.timestampsOn === undefined) {
      // Values are stored as strings, so we'll need to convert it
      return UserSessionStore.get(USER_SESSION_STORE_KEYS.TIMESTAMPS_ON) === "true";
    }

    return state.timestampsOn;
  }, [state]);

  const handleJobLogChange = useCallback((log: Job) => {
    setState((state) => ({
      ...state,
      linesHaveTimestamps: log.linesHaveTimestamps,
    }));
  }, []);

  const handleThemeToggle = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();

    const currentTheme = getTheme();
    const newTheme = currentTheme === "default" ? "solarized" : "default";

    setState((state) => ({ ...state, theme: newTheme }));
    UserSessionStore.set(USER_SESSION_STORE_KEYS.THEME, newTheme);
  }, []);

  const handleClickFollowLog = useCallback((evt: React.MouseEvent<HTMLButtonElement>) => {
    evt.preventDefault();

    setState((state) => ({ ...state, following: !state.following }));
  }, []);

  const handleClickExpandGroups = useCallback((evt: React.MouseEvent<HTMLButtonElement>) => {
    evt.preventDefault();

    disableFollow();

    // @ts-expect-error - TS2554 - Expected 6 arguments, but got 2.
    LegacyDispatcher.emit("job_log_group:expand_all", {
      jobId: props.job.id,
    });
  }, []);

  const handleClickCollapseLogs = useCallback((evt: React.MouseEvent<HTMLButtonElement>) => {
    evt.preventDefault();

    disableFollow();

    // @ts-expect-error - TS2554 - Expected 6 arguments, but got 2.
    LegacyDispatcher.emit("job_log_group:collapse_all", {
      jobId: props.job.id,
    });
  }, []);

  const handleClickToggleTimestamps = useCallback(
    (evt: React.MouseEvent<HTMLButtonElement>) => {
      evt.preventDefault();

      const timestampsOn = !isTimestampsOn;

      setState((state) => ({ ...state, timestampsOn }));

      // Store it so next time we default to that choice
      UserSessionStore.set(USER_SESSION_STORE_KEYS.TIMESTAMPS_ON, timestampsOn.toString());
    },
    [isTimestampsOn],
  );

  const contentRef = useRef<HTMLDivElement>(null);

  // In the new UI the log table is itself the scroll viewport; the tbody holds
  // the (growing) virtualised content that scroll-following watches for resize.
  const logTable = Features.BuildSidebar
    ? document.querySelector<HTMLTableElement>(".JobLogOutputComponent__Table")
    : null;

  const contentElement = Features.BuildSidebar
    ? logTable?.querySelector<HTMLElement>("tbody") ?? null
    : contentRef.current;

  const scrollContainer = Features.BuildSidebar ? logTable : document;

  useScrollFollow({
    follow: state.following,
    onCancel: disableFollow,
    scrollContainer: scrollContainer,
    contentElement,
  });

  // If we've got multiple JobLogs open and we change the theme in one, then we should
  // make sure that all instances also switch theme. Fortunately the UserSessionStore
  // has our back, emitting an event when cached state is changed.
  UserSessionStore.on("change", (event) => {
    if (event.key === USER_SESSION_STORE_KEYS.THEME) {
      setState((state) => ({ ...state, theme: event.newValue }));
    }
  });

  // In the new UI, allow following to continue when expanding/collapsing log groups.
  if (!Features.BuildSidebar) {
    LegacyDispatcher.on("job_log_group:toggle", () => {
      disableFollow();
    });
  }

  if (props.job.logDeletedMessage) {
    return (
      <Log className="min-h-0 p-0">
        <LogBody>
          <LogMessage>
            <Emojify text={props.job.logDeletedMessage} />
          </LogMessage>
        </LogBody>
      </Log>
    );
  }

  const followLogButtonNode = isJobFinished(props.job) ? (
    <LogControl onClick={handleClickFollowLog} className="shrink-0">
      <Icon icon="heroicons/16/solid/arrow-down" className="!mr-1 h-4 w-4" />
      {Features.BuildSidebar ? "Scroll to end" : "Jump to end"}
    </LogControl>
  ) : state.following ? (
    <LogControl onClick={handleClickFollowLog} active={true} className="shrink-0">
      <Icon icon="heroicons/16/solid/arrow-down" className="!mr-1 h-4 w-4" />
      Following…
    </LogControl>
  ) : (
    <LogControl onClick={handleClickFollowLog} className="shrink-0">
      <Icon icon="heroicons/16/solid/arrow-down" className="!mr-1 h-4 w-4" />
      Follow
    </LogControl>
  );

  return (
    <div
      ref={contentRef}
      data-testid="JobLogComponent"
      data-theme={state.theme}
      className={classNames("JobLogComponent not-inverted", {
        "mt-[10px]": !Features.BuildSidebar,
        "flex min-h-0 flex-auto flex-col": Features.BuildSidebar,
      })}
    >
      <LogSearchProvider>
        <JobLogOutput
          job={props.job}
          onChange={handleJobLogChange}
          timestampsOn={isTimestampsOn}
          following={state.following}
          onDisableFollow={disableFollow}
          onFailureMarkersChange={handleFailureMarkersChange}
        >
          <LogControl onClick={handleClickExpandGroups}>
            <Icon icon="heroicons/16/solid/plus" className="h-4 w-4" />
            Expand groups
          </LogControl>

          <LogControl onClick={handleClickCollapseLogs}>
            <Icon icon="heroicons/16/solid/minus" className="h-4 w-4" />
            Collapse groups
          </LogControl>

          {state.linesHaveTimestamps && (
            <LogControl onClick={handleClickToggleTimestamps}>
              <Icon icon="heroicons/16/solid/calendar" className="h-4 w-4" />
              {isTimestampsOn ? "Hide timestamps" : "Show timestamps"}
            </LogControl>
          )}

          <LogControl className="lg:ml-auto" onClick={handleThemeToggle}>
            <Icon
              icon={
                state.theme === "default" ? "heroicons/16/solid/sun" : "heroicons/16/solid/moon"
              }
              className="h-4 w-4"
            />
            Theme
          </LogControl>

          {["finished", "canceled", "timed_out"].includes(props.job.state) &&
            props.job.permissions.deleteJobLog.allowed && (
              <LogControl
                href={props.job.basePath + "/delete_log"}
                data-method="delete"
                data-confirm="Permanently delete this job’s log?"
              >
                <Icon icon="heroicons/16/solid/trash" className="h-4 w-4" />
                Delete
              </LogControl>
            )}

          <LogControl href={props.job.basePath + "/download.txt"}>
            <Icon icon="heroicons/16/solid/arrow-down-tray" className="h-4 w-4" />
            Download
          </LogControl>

          <LogControl href={props.job.basePath + "/log"} target="_blank">
            <Icon icon="heroicons/16/solid/arrow-top-right-on-square" className="mr-1 h-4 w-4" />
            Open
          </LogControl>

          {!Features.BuildSidebar && followLogButtonNode}

          {Features.BuildSidebar && <LogSearch onOpen={disableFollow} />}
        </JobLogOutput>
      </LogSearchProvider>

      <div
        className={classNames("JobLogComponent__Footer flex items-start gap-4 text-xs/5", {
          "shrink-0 border-t border-[var(--theme-bg-2)] bg-[var(--theme-bg-1)] px-2.5":
            Features.BuildSidebar,
        })}
      >
        <div className="mr-auto">
          <JobLogExitInformation
            // @ts-expect-error - TS2769 - No overload matches this call.
            className={classNames("JobLogComponent__ExitStatus py-2", {
              "text-[var(--theme-text-1)]": Features.BuildSidebar,
              "text-charcoal-300": !Features.BuildSidebar,
            })}
            job={props.job}
          />
        </div>

        {jumpToFailure && (
          <LogControl
            onClick={(event) => {
              event.preventDefault();
              jumpToFailure.scrollToNext();
            }}
            className={classNames(
              "flex shrink-0 items-center py-2 hover:no-underline focus:no-underline active:no-underline",
              {
                "hover:text-[var(--theme-text-1)]": Features.BuildSidebar,
                "text-charcoal-300 hover:text-current": !Features.BuildSidebar,
              },
            )}
          >
            <span className="mr-1 tabular-nums">
              [
              <span className="text-red-500">
                {jumpToFailure.current && jumpToFailure.count > 1
                  ? `${jumpToFailure.current}/${jumpToFailure.count >= 100 ? "99+" : jumpToFailure.count}`
                  : jumpToFailure.count >= 100
                    ? "99+"
                    : jumpToFailure.count}
              </span>
              ]
            </span>
            {jumpToFailure.count === 1 ? "Test failure" : "Test failures"}
          </LogControl>
        )}

        <LogControl
          href={`#${props.parentElementId}`}
          onClick={(event) => {
            // Scroll rather than fragment hash link in new UI
            if (Features.BuildSidebar) {
              event.preventDefault();
              disableFollow();
              (scrollContainer as HTMLElement)?.scrollTo({ top: 0 });
            }
          }}
          className={classNames(
            "flex shrink-0 items-center py-2 hover:no-underline focus:no-underline active:no-underline",
            {
              "hover:text-[var(--theme-text-1)]": Features.BuildSidebar,
              "text-charcoal-300 hover:text-current": !Features.BuildSidebar,
            },
          )}
        >
          <Icon icon="heroicons/16/solid/arrow-up" className="mr-1 h-4 w-4" />
          {Features.BuildSidebar ? "Scroll to top" : "Back to Job"}
        </LogControl>

        {Features.BuildSidebar && followLogButtonNode}
      </div>
    </div>
  );
}
